import React, { useEffect, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  TimePicker,
  Typography,
  Upload,
  message
} from 'antd';
import {
  AimOutlined,
  CarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  PictureOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import eventService from '../../services/eventService';
import { getCurrentLocation } from '../../utils/locationUtils';
import { loadCountriesConfig } from '../../utils/countriesUtils';
import EventPhoneField from './EventPhoneField';
import {
  getCustomFieldName,
  getFieldRules,
  parsePhoneValue,
  phoneValueToString
} from '../../utils/eventWizardUtils';

const { Text, Title } = Typography;
const { TextArea } = Input;

const SectionHeading = ({ icon, title, description }) => (
  <div className="event-wizard-heading">
    <Title level={3}>{icon}{title}</Title>
    {description ? <Text type="secondary">{description}</Text> : null}
  </div>
);

const formatReviewValue = (value) => {
  if (value === undefined || value === null || value === '') return 'Not set';
  if (dayjs.isDayjs(value)) return value.format('DD MMM YYYY HH:mm');
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value?.currency) return `${value.currency} ${value.value ?? ''}`.trim();
  if (value?.dialCode) return phoneValueToString(value) || 'Not set';
  return String(value);
};

export const EventBasicsStep = ({
  form,
  formData,
  updateFormData,
  lookups,
  companies,
  oems,
  loading,
  userRole
}) => {
  const isConsultant = userRole === 'CONSULTANT';
  const selectedType = (lookups?.eventTypes || []).find(
    (item) => Number(item.id) === Number(formData.eventTypeId)
  );

  const handleCompanyChange = (companyId) => {
    const selectedCompany = companies.find((company) => String(company.id) === String(companyId)) || null;
    const resetValues = {
      selectedCompanyId: companyId,
      selectedCompany,
      selectedDriverId: undefined,
      selectedDriver: null,
      driverName: '',
      driverPhone: undefined,
      horse: null,
      trailer1: null,
      trailer2: null,
      horseFault: false,
      trailer1Fault: false,
      trailer2Fault: false
    };
    updateFormData(resetValues);
    form.setFieldsValue(resetValues);
  };

  const handleTypeChange = (eventTypeId) => {
    const type = (lookups?.eventTypes || []).find((item) => Number(item.id) === Number(eventTypeId));
    const patch = { eventTypeId };
    if (!type?.isPlanned) patch.plannedDate = null;
    updateFormData(patch);
    form.setFieldsValue(patch);
  };

  return (
    <div>
      <SectionHeading
        icon={<FileTextOutlined />}
        title="Event Information"
        description="Choose the event configuration and capture the requester details."
      />

      <div className="event-wizard-form-grid">
        {isConsultant ? (
          <Form.Item label="OEM Name" name="oemName" rules={[{ required: true, message: 'Please select an OEM' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={[
                { value: 'No OEM', label: 'No OEM' },
                ...oems.map((oem) => ({ value: oem.oem, label: oem.oem }))
              ]}
              loading={loading}
              placeholder="Select OEM"
            />
          </Form.Item>
        ) : null}

        <Form.Item label="Type" name="eventTypeId" rules={[{ required: true, message: 'Please select a type' }]}>
          <Select
            options={(lookups?.eventTypes || []).map((item) => ({
              value: Number(item.id),
              label: item.name
            }))}
            onChange={handleTypeChange}
            loading={loading}
            placeholder="Select type"
          />
        </Form.Item>

        {selectedType?.isPlanned ? (
          <Form.Item label="Schedule Date" name="plannedDate" rules={[{ required: true, message: 'Please select a schedule date' }]}>
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        ) : null}

        <Form.Item label="Involvement" name="involvementId" rules={[{ required: true, message: 'Please select involvement' }]}>
          <Select
            options={(lookups?.eventInvolvements || []).map((item) => ({
              value: Number(item.id),
              label: item.name
            }))}
            loading={loading}
            placeholder="Select involvement"
          />
        </Form.Item>

        <Form.Item label="Event Location" name="eventLocationId" rules={[{ required: true, message: 'Please select event location' }]}>
          <Select
            options={(lookups?.eventLocations || []).map((item) => ({
              value: Number(item.id),
              label: item.name
            }))}
            loading={loading}
            placeholder="Select event location"
          />
        </Form.Item>

        <Form.Item label="Customer" name="selectedCompanyId" rules={[{ required: true, message: 'Please select a customer' }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={companies.map((company) => ({ value: company.id, label: company.name }))}
            onChange={handleCompanyChange}
            loading={loading}
            placeholder="Select customer"
          />
        </Form.Item>

        <Form.Item label="Controller Name" name="controllerName" rules={[{ required: true, message: 'Please enter the controller name' }]}>
          <Input placeholder="Controller name" />
        </Form.Item>

        <Form.Item
          label="Controller Contact"
          name="controllerPhone"
          required
          rules={[{
            validator: (_, value) => String(value?.nationalNumber || '').trim()
              ? Promise.resolve()
              : Promise.reject(new Error('Please enter the controller contact number'))
          }]}
        >
          <EventPhoneField placeholder="Enter phone number" />
        </Form.Item>
      </div>
    </div>
  );
};

export const EventDriverStep = ({ form, formData, updateFormData }) => {
  const [drivers, setDrivers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCountriesConfig().then(setCountries).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!formData.selectedCompanyId) {
      setDrivers([]);
      return undefined;
    }

    setLoading(true);
    eventService.getCompanyDrivers(formData.selectedCompanyId)
      .then((rows) => {
        if (!cancelled) setDrivers((rows || []).filter((driver) => !driver.deleted && !driver.inactive));
      })
      .catch((error) => {
        console.error('Failed to load company drivers:', error);
        message.error('Failed to load drivers');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formData.selectedCompanyId]);

  const handleDriverChange = (driverId) => {
    const driver = drivers.find((item) => String(item.id) === String(driverId));
    const values = {
      selectedDriverId: driverId,
      selectedDriver: driver || null,
      driverName: driver?.fullName || '',
      driverPhone: parsePhoneValue(driver?.mobileNumber, countries)
    };
    updateFormData(values);
    form.setFieldsValue(values);
  };

  return (
    <div>
      <SectionHeading
        icon={<TeamOutlined />}
        title="Driver Information"
        description={`Select a driver for ${formData.selectedCompany?.name || 'the chosen customer'}.`}
      />

      {!formData.selectedCompanyId ? (
        <Alert type="warning" showIcon message="Select a customer before choosing a driver." />
      ) : null}

      <Form.Item label="Driver" name="selectedDriverId" rules={[{ required: true, message: 'Please select a driver' }]}>
        <Select
          showSearch
          optionFilterProp="label"
          options={drivers.map((driver) => ({ value: driver.id, label: driver.fullName }))}
          loading={loading}
          disabled={!formData.selectedCompanyId}
          onChange={handleDriverChange}
          placeholder="Select driver"
          notFoundContent={loading ? <Spin size="small" /> : 'No drivers found'}
        />
      </Form.Item>

      <Form.Item label="Driver Name" name="driverName" rules={[{ required: true, message: 'Please enter the driver name' }]}>
        <Input disabled={!formData.selectedDriverId} placeholder="Driver name" />
      </Form.Item>

      <Form.Item
        label="Driver Contact"
        name="driverPhone"
        required
        rules={[{
          validator: (_, value) => String(value?.nationalNumber || '').trim()
            ? Promise.resolve()
            : Promise.reject(new Error('Please enter the driver contact number'))
        }]}
      >
        <EventPhoneField disabled={!formData.selectedDriverId} placeholder="Enter phone number" />
      </Form.Item>
    </div>
  );
};

const AssetPicker = ({
  title,
  kind,
  companyId,
  selectedAsset,
  faultChecked,
  selecting,
  onSelect,
  onClear,
  onFaultChange,
  onFieldChange
}) => {
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const wantsTrailer = kind !== 'horse';

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!companyId) {
        setOptions([]);
        return;
      }
      setLoading(true);
      eventService.searchCompanyVehicles(companyId, searchTerm)
        .then((rows) => {
          if (cancelled) return;
          const filtered = (rows || []).filter((vehicle) => {
            const isTrailer = Number(vehicle.trailer ?? vehicle.isTrailer ?? 0) === 1;
            return wantsTrailer ? isTrailer : !isTrailer;
          });
          setOptions(filtered.slice(0, 50));
        })
        .catch((error) => {
          console.error(`Failed to load ${title.toLowerCase()} assets:`, error);
          if (!cancelled) setOptions([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, searchTerm ? 500 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [companyId, searchTerm, title, wantsTrailer]);

  const selectOptions = options.map((vehicle) => ({
    value: vehicle.id,
    label: [vehicle.registration, vehicle.fleet, vehicle.vin].filter(Boolean).join(' | '),
    vehicle
  }));

  return (
    <Card
      size="small"
      className={`event-wizard-asset-card${selectedAsset ? ' is-selected' : ''}`}
      title={<Space><CarOutlined />{title}</Space>}
      extra={selectedAsset ? <Button type="link" danger onClick={onClear}>Remove</Button> : null}
    >
      <Select
        showSearch
        filterOption={false}
        onSearch={setSearchTerm}
        onChange={(_, option) => onSelect(option.vehicle)}
        options={selectOptions}
        value={selectedAsset?.id}
        loading={loading || selecting}
        disabled={!companyId || selecting}
        placeholder={`Search ${title.toLowerCase()} by registration, fleet or VIN`}
        notFoundContent={loading ? <Spin size="small" /> : 'No matching assets'}
        style={{ width: '100%' }}
      />

      {selectedAsset ? (
        <div className="event-wizard-asset-details">
          <Checkbox checked={faultChecked} onChange={(event) => onFaultChange(event.target.checked)}>
            Fault applies to this asset
          </Checkbox>
          <div className="event-wizard-form-grid compact">
            <label>Registration<Input value={selectedAsset.registration || ''} onChange={(event) => onFieldChange('registration', event.target.value)} /></label>
            <label>Fleet<Input value={selectedAsset.fleet || ''} onChange={(event) => onFieldChange('fleet', event.target.value)} /></label>
            <label>VIN<Input value={selectedAsset.vin || ''} onChange={(event) => onFieldChange('vin', event.target.value)} /></label>
            <label>Make<Input value={selectedAsset.make || ''} onChange={(event) => onFieldChange('make', event.target.value)} /></label>
            <label>Model<Input value={selectedAsset.model || ''} onChange={(event) => onFieldChange('model', event.target.value)} /></label>
            <label>Year<Input value={selectedAsset.year || ''} inputMode="numeric" onChange={(event) => onFieldChange('year', event.target.value)} /></label>
            {kind === 'horse' ? (
              <>
                <label>Mileage<InputNumber value={selectedAsset.mileage ?? null} min={0} style={{ width: '100%' }} onChange={(value) => onFieldChange('mileage', value)} /></label>
                <label>Lease Type<Input value={selectedAsset.leaseType || ''} onChange={(event) => onFieldChange('leaseType', event.target.value)} /></label>
                <label>Cost Centre<Input value={selectedAsset.costCentreName || ''} onChange={(event) => onFieldChange('costCentreName', event.target.value)} /></label>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
};

export const EventAssetsStep = ({
  formData,
  updateFormData,
  onAssetSelect,
  telematicsLoading,
  showAssetError,
  assetErrorRef
}) => {
  const setAssetField = (key, field, value) => {
    updateFormData({ [key]: { ...formData[key], [field]: value } });
  };

  const clearAsset = (key, faultKey) => updateFormData({ [key]: null, [faultKey]: false });

  return (
    <div>
      <SectionHeading
        icon={<CarOutlined />}
        title="Event Assets"
        description="Select at least one horse or trailer involved in this event."
      />

      {showAssetError ? (
        <div ref={assetErrorRef} className="event-wizard-assets-error">
          <Alert type="error" showIcon message="Select at least one horse or trailer before continuing." />
        </div>
      ) : null}

      {!formData.selectedCompanyId ? (
        <Alert type="warning" showIcon message="Select a customer before choosing assets." />
      ) : null}

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <AssetPicker
          title="Horse"
          kind="horse"
          companyId={formData.selectedCompanyId}
          selectedAsset={formData.horse}
          faultChecked={formData.horseFault}
          selecting={telematicsLoading}
          onSelect={(vehicle) => onAssetSelect('horse', vehicle)}
          onClear={() => clearAsset('horse', 'horseFault')}
          onFaultChange={(checked) => updateFormData({ horseFault: checked })}
          onFieldChange={(field, value) => setAssetField('horse', field, value)}
        />
        <AssetPicker
          title="Trailer 1"
          kind="trailer1"
          companyId={formData.selectedCompanyId}
          selectedAsset={formData.trailer1}
          faultChecked={formData.trailer1Fault}
          onSelect={(vehicle) => onAssetSelect('trailer1', vehicle)}
          onClear={() => clearAsset('trailer1', 'trailer1Fault')}
          onFaultChange={(checked) => updateFormData({ trailer1Fault: checked })}
          onFieldChange={(field, value) => setAssetField('trailer1', field, value)}
        />
        <AssetPicker
          title="Trailer 2"
          kind="trailer2"
          companyId={formData.selectedCompanyId}
          selectedAsset={formData.trailer2}
          faultChecked={formData.trailer2Fault}
          onSelect={(vehicle) => onAssetSelect('trailer2', vehicle)}
          onClear={() => clearAsset('trailer2', 'trailer2Fault')}
          onFaultChange={(checked) => updateFormData({ trailer2Fault: checked })}
          onFieldChange={(field, value) => setAssetField('trailer2', field, value)}
        />
      </Space>
    </div>
  );
};

export const EventLocationStep = ({ form, formData, updateFormData, telematicsLoading }) => {
  const [addressOptions, setAddressOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (searchTerm.trim().length < 3) {
      setAddressOptions([]);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSearching(true);
      eventService.autocompleteAddress(searchTerm)
        .then((rows) => {
          if (cancelled) return;
          setAddressOptions((rows || []).map((item) => ({
            value: item?.placePrediction?.text?.text || '',
            label: item?.placePrediction?.text?.text || '',
            placeId: item?.placePrediction?.placeId
          })).filter((item) => item.value && item.placeId));
        })
        .catch((error) => {
          console.error('Address search failed:', error);
          if (!cancelled) setAddressOptions([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchTerm]);

  const applyLocation = (address, latitude, longitude, extra = {}) => {
    const values = { address, latitude, longitude, ...extra };
    updateFormData(values);
    form.setFieldsValue(values);
  };

  const handleAddressSelect = async (address, option) => {
    setSearching(true);
    try {
      const result = await eventService.geocodePlace(option.placeId);
      const latitude = result?.geometry?.location?.lat;
      const longitude = result?.geometry?.location?.lng;
      applyLocation(
        result?.custom_formatted_address || result?.formatted_address || address,
        latitude,
        longitude,
        { isTelematicsLocation: 0, fmVehicleId: 0 }
      );
    } catch (error) {
      console.error('Failed to resolve address:', error);
      message.error('Failed to resolve the selected address');
    } finally {
      setSearching(false);
    }
  };

  const useDeviceLocation = async () => {
    setLocating(true);
    try {
      const current = await getCurrentLocation();
      if (!current) {
        message.warning('Location permission is required');
        return;
      }
      const result = await eventService.reverseGeocode(current.latitude, current.longitude);
      applyLocation(
        result?.custom_formatted_address || result?.formatted_address || `${current.latitude}, ${current.longitude}`,
        current.latitude,
        current.longitude,
        { isTelematicsLocation: 0, fmVehicleId: 0 }
      );
      message.success('Current location captured');
    } catch (error) {
      console.error('Failed to capture current location:', error);
      message.error('Failed to capture current location');
    } finally {
      setLocating(false);
    }
  };

  return (
    <div>
      <SectionHeading
        icon={<EnvironmentOutlined />}
        title="Physical Location"
        description="Confirm the address and coordinates where the event is taking place."
      />

      {formData.isTelematicsLocation ? (
        <Alert
          type="success"
          showIcon
          message="Location supplied by vehicle telematics"
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {telematicsLoading ? <Alert type="info" showIcon message="Fetching vehicle telematics location..." style={{ marginBottom: 16 }} /> : null}

      <Form.Item label="Address" name="address" rules={[{ required: true, message: 'Please enter or select an address' }]}>
        <AutoComplete
          options={addressOptions}
          onSearch={setSearchTerm}
          onSelect={handleAddressSelect}
          onChange={(address) => updateFormData({ address })}
          placeholder="Search for an address"
          notFoundContent={searching ? <Spin size="small" /> : null}
        >
          <Input suffix={<SearchOutlined />} />
        </AutoComplete>
      </Form.Item>

      <div className="event-wizard-form-grid">
        <Form.Item label="Latitude" name="latitude" rules={[{ required: true, message: 'Latitude is required' }]}>
          <InputNumber min={-90} max={90} precision={6} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Longitude" name="longitude" rules={[{ required: true, message: 'Longitude is required' }]}>
          <InputNumber min={-180} max={180} precision={6} style={{ width: '100%' }} />
        </Form.Item>
      </div>

      <Button icon={<AimOutlined />} block onClick={useDeviceLocation} loading={locating}>
        Use My Location
      </Button>
    </div>
  );
};

const CurrencyField = ({ value, onChange, options }) => (
  <Space.Compact className="event-currency-field">
    <Select
      value={value?.currency || 'ZAR'}
      options={options}
      showSearch
      optionFilterProp="label"
      popupMatchSelectWidth={false}
      onChange={(currency) => onChange?.({ currency, value: value?.value ?? null })}
    />
    <InputNumber
      value={value?.value ?? null}
      min={0}
      precision={2}
      style={{ width: '100%' }}
      onChange={(amount) => onChange?.({ currency: value?.currency || 'ZAR', value: amount })}
    />
  </Space.Compact>
);

const renderCustomInput = (field, currencyOptions) => {
  const rules = getFieldRules(field);
  const options = (rules.options || []).map((option) => ({ value: option, label: option }));

  switch (field.fieldType) {
    case 'TextArea':
      return <TextArea rows={3} placeholder={field.fieldHint} />;
    case 'Number':
      return <InputNumber style={{ width: '100%' }} placeholder={field.fieldHint} />;
    case 'Select':
      return <Select options={options} placeholder={field.fieldHint} />;
    case 'RadioButton':
      return <Radio.Group options={options} />;
    case 'Checkbox':
      return <Checkbox.Group options={options} className="event-custom-checkboxes" />;
    case 'Toggle':
      return <Switch />;
    case 'DatePicker':
      return <DatePicker showTime={Boolean(rules.includeTime)} style={{ width: '100%' }} placeholder={field.fieldHint} />;
    case 'TimePicker':
      return <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder={field.fieldHint} />;
    case 'Currency':
      return <CurrencyField options={currencyOptions} />;
    case 'PhoneNumber':
      return <EventPhoneField placeholder={field.fieldHint || 'Phone number'} />;
    case 'Text':
    default:
      return <Input placeholder={field.fieldHint} />;
  }
};

export const EventDetailsAndCustomStep = ({ customFields, currencyOptions, loading }) => (
  <div>
    <SectionHeading
      icon={<FileTextOutlined />}
      title="Details and Custom Fields"
      description="Describe the event and complete the fields configured for its type."
    />

    <Form.Item label="Describe the Event" name="description" rules={[{ required: true, message: 'Please enter a description' }]}>
      <TextArea rows={5} maxLength={2000} showCount placeholder="Describe the event" />
    </Form.Item>
    <Form.Item label="Internal Case Notes" name="internalNotes">
      <TextArea rows={3} maxLength={1000} showCount placeholder="Internal case notes" />
    </Form.Item>
    <Form.Item label="Other Event Information" name="otherEventInfo">
      <TextArea rows={3} maxLength={1000} showCount placeholder="Other event information" />
    </Form.Item>
    <Form.Item label="Order Number" name="orderNumber">
      <Input placeholder="Order number" />
    </Form.Item>

    <Divider orientation="left">Custom Fields</Divider>
    {loading ? (
      <div className="event-wizard-centered"><Spin tip="Loading custom fields..." /></div>
    ) : customFields.length ? (
      customFields.map((field) => {
        const rules = getFieldRules(field);
        const requiredRule = rules.required
          ? [{ required: true, message: `${field.fieldName} is required` }]
          : [];
        const phoneRule = field.fieldType === 'PhoneNumber' && rules.required
          ? [{
            validator: (_, value) => value?.nationalNumber
              ? Promise.resolve()
              : Promise.reject(new Error(`${field.fieldName} is required`))
          }]
          : requiredRule;

        return (
          <Form.Item
            key={field.id}
            label={field.fieldName}
            name={getCustomFieldName(field)}
            valuePropName={field.fieldType === 'Toggle' ? 'checked' : 'value'}
            rules={phoneRule}
          >
            {renderCustomInput(field, currencyOptions)}
          </Form.Item>
        );
      })
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No custom fields for this event type" />
    )}
  </div>
);

export const EventMediaStep = ({ formData, updateFormData }) => {
  const fileList = formData.imageryFiles || [];

  return (
    <div>
      <SectionHeading
        icon={<PictureOutlined />}
        title="Event Media"
        description="Add optional photos, videos, or PDF documents."
      />
      <Upload
        listType="picture-card"
        multiple
        accept="image/*,video/*,.pdf,application/pdf"
        beforeUpload={() => false}
        fileList={fileList}
        onChange={({ fileList: nextFiles }) => updateFormData({ imageryFiles: nextFiles })}
      >
        <div><PlusOutlined /><div style={{ marginTop: 8 }}>Add Media</div></div>
      </Upload>
      <Text type="secondary">{fileList.length} file{fileList.length === 1 ? '' : 's'} selected</Text>
    </div>
  );
};

const assetSummary = (asset) => asset
  ? [asset.registration, asset.fleet, asset.make, asset.model].filter(Boolean).join(' | ')
  : 'Not selected';

export const EventSummaryStep = ({ formData, lookups, customFields }) => {
  const eventType = (lookups?.eventTypes || []).find((item) => Number(item.id) === Number(formData.eventTypeId));
  const involvement = (lookups?.eventInvolvements || []).find((item) => Number(item.id) === Number(formData.involvementId));
  const eventLocation = (lookups?.eventLocations || []).find((item) => Number(item.id) === Number(formData.eventLocationId));

  return (
    <div>
      <SectionHeading
        icon={<UserOutlined />}
        title="Review and Submit"
        description="Confirm the captured information before creating the event."
      />

      <Card size="small" title="Event" className="event-wizard-summary-card">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Type">{eventType?.name || 'Not set'}</Descriptions.Item>
          {eventType?.isPlanned ? <Descriptions.Item label="Schedule">{formatReviewValue(formData.plannedDate)}</Descriptions.Item> : null}
          <Descriptions.Item label="Involvement">{involvement?.name || 'Not set'}</Descriptions.Item>
          <Descriptions.Item label="Event Location">{eventLocation?.name || 'Not set'}</Descriptions.Item>
          <Descriptions.Item label="Customer">{formData.selectedCompany?.name || 'Not set'}</Descriptions.Item>
          <Descriptions.Item label="Controller">{formData.controllerName || 'Not set'}</Descriptions.Item>
          <Descriptions.Item label="Controller Contact">{formatReviewValue(formData.controllerPhone)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Driver and Assets" className="event-wizard-summary-card">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Driver">{formData.driverName || 'Not set'}</Descriptions.Item>
          <Descriptions.Item label="Driver Contact">{formatReviewValue(formData.driverPhone)}</Descriptions.Item>
          <Descriptions.Item label="Horse">{assetSummary(formData.horse)}</Descriptions.Item>
          <Descriptions.Item label="Trailer 1">{assetSummary(formData.trailer1)}</Descriptions.Item>
          <Descriptions.Item label="Trailer 2">{assetSummary(formData.trailer2)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Location and Details" className="event-wizard-summary-card">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Address">{formData.address || 'Not set'}</Descriptions.Item>
          <Descriptions.Item label="Coordinates">
            {formData.latitude !== '' && formData.longitude !== ''
              ? `${formData.latitude}, ${formData.longitude}`
              : 'Not set'}
          </Descriptions.Item>
          <Descriptions.Item label="Description">{formData.description || 'Not set'}</Descriptions.Item>
          <Descriptions.Item label="Media">{(formData.imageryFiles || []).length}</Descriptions.Item>
        </Descriptions>
      </Card>

      {customFields.length ? (
        <Card size="small" title="Custom Fields" className="event-wizard-summary-card">
          <Descriptions column={1} size="small">
            {customFields.map((field) => (
              <Descriptions.Item key={field.id} label={field.fieldName}>
                {formatReviewValue(formData[getCustomFieldName(field)])}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>
      ) : null}

      <Alert type="success" showIcon message="Ready to create this event" />
    </div>
  );
};
